import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {faCircleXmark, faSquare, faSquareCheck, faX} from "@fortawesome/pro-solid-svg-icons";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  AuthControllerService,
  Organisation,
  OrganisationControllerService,
  Privilege,
  ProjectControllerService,
  Role
} from "../../../api";
import {SelectionModel} from "@angular/cdk/collections";
import {StateConfigService} from "../../../services/state/state-config.service";
import {RoleAndPrivilegesPipe} from "../../../pipes/role-and-privileges.pipe";
import {
  BicButtonComponent,
  BicInputComponent,
  BicPopupComponent,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";

@Component({
  selector: 'app-custom-role-detail',
  templateUrl: './custom-role-detail.component.html',
  styleUrls: ['./custom-role-detail.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    RoleAndPrivilegesPipe,
    BicButtonComponent,
    BicInputComponent,
    BicPopupComponent,
    TooltipDirective
  ]
})
export class CustomRoleDetailComponent implements OnInit{
  private readonly hiddenLegacyPlaceholders = new Set([
    'PROJECT_QUALITY_CHECK',
    'PROJECT_HOAI_CHECK',
    'PROJECT_BIM_TOOLS_CHECK',
    // Aufgegangen in „myCheck verwalten" / „myCheck löschen" – werden nirgends mehr geprüft.
    'PROJECT_CHECKRULE_MANAGE',
    'PROJECT_CHECKRULE_DELETE',
  ]);
  data = inject<{
    role?: Role;
    isProject: boolean;
    organisation: Organisation;
    onDelete?: (role?: Role) => void;
    isAssigned?: boolean;
  }>(MAT_DIALOG_DATA);

  get isAssigned(): boolean {
    return !!this.data.isAssigned;
  }
  private authControllerService = inject(AuthControllerService);
  private projectControllerService = inject(ProjectControllerService);
  private organisationControllerService = inject(OrganisationControllerService);
  private stateService = inject(StateConfigService);
  private dialogRef = inject<MatDialogRef<CustomRoleDetailComponent>>(MatDialogRef);


  form = new FormGroup({
    roleTitle: new FormControl()
  });

  isExistingRole = false;
  isProject = false;

  privileges: Privilege[] = []
  role: Role | undefined

  selectedPrivileges: SelectionModel<String> = new SelectionModel<String>(true, []);



  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faSquare = faSquare;
  protected readonly faSquareCheck = faSquareCheck;

  ngOnInit() {
    this.isProject = this.data.isProject;

    this.isExistingRole = false
    if(this.data.role) {
      this.role = this.data.role
      this.form.controls.roleTitle.setValue(this.role?.name)


      this.role?.privileges?.forEach(privilege => {
        this.selectedPrivileges.select(privilege.id!);
      });

      this.isExistingRole = true
    }


    this.authControllerService.privileges().subscribe((privileges) => {
        this.privileges = privileges;
      }
    )
  }


  saveChanges() {
    const role: Role = {
      id: this.role?.id,
      name: this.form.controls.roleTitle.value,
      privileges: this.privileges.filter(privilege => this.selectedPrivileges.isSelected(privilege.id!))
    }


    if(this.isProject && !this.isExistingRole) {
      this.projectControllerService.createRole(role, this.stateService.getProjectId()).subscribe(( role) => {
        this.dialogRef.close(role);
      })
    }else if(this.isProject && this.isExistingRole) {
      this.projectControllerService.updateRole(role, this.stateService.getProjectId()).subscribe(() => {
        this.dialogRef.close();
      })
    }else if(!this.isProject && !this.isExistingRole) {
      this.organisationControllerService.createRole1(role, this.data.organisation.id!).subscribe((role) => {
        this.dialogRef.close(role);
      })
    }else if(!this.isProject && this.isExistingRole) {
      this.organisationControllerService.updateRole1(role, this.data.organisation.id!).subscribe(() => {
        this.dialogRef.close();
      })
    }
  }
  callDelete() {
    if (this.data.onDelete && this.role) {
      this.data.onDelete(this.role);
      this.dialogRef.close();
    }
  }
  get allPrivileges() {
    if (this.isProject) {
      return this.projectPrivileges;
    }else {
      return this.organisationPrivileges;
    }
  }

  get projectPrivileges() {
    return this.privileges.filter(privilege => privilege.type == "PROJECT" && !this.isLegacyPlaceholderPrivilege(privilege));
  }

  get organisationPrivileges() {
    return this.privileges.filter(privilege => privilege.type == "ORGANISATION" && !this.isLegacyPlaceholderPrivilege(privilege));
  }

  get customPrivileges() {
    return this.privileges.filter(privilege => privilege.type == "CUSTOM" && !this.isLegacyPlaceholderPrivilege(privilege));

  }

  private isLegacyPlaceholderPrivilege(privilege: Privilege): boolean {
    return this.hiddenLegacyPlaceholders.has(privilege.id ?? '') || this.hiddenLegacyPlaceholders.has(privilege.name ?? '');
  }

  checkInputFilled() {
    return (!(!this.form.controls.roleTitle.value || this.form.controls.roleTitle.value == ""));
  }

  close() {
    this.dialogRef.close();
  }

  selectPrivilege(privilege: Privilege) {
    this.selectedPrivileges.toggle(privilege.id!);
  }

  isSelected(privilege: Privilege) {
    return this.selectedPrivileges.isSelected(privilege.id!);
  }


  get hasUserManagementPrivilege() {
    if (this.isProject) {
      return this.selectedPrivileges.selected.some(privilege => privilege == "PROJECT_USER_MANAGEMENT")
    }else {
      return this.selectedPrivileges.selected.some(privilege => privilege == "ORGANISATION_USER_MANAGEMENT")
    }
  }

  protected readonly faX = faX;
}
